import './LineChart.css';
import React from 'react'
// import ReactResizeDetector from 'react-resize-detector';
import { createChart, isBusinessDay, TickMarkType } from 'lightweight-charts';
import { PriceLineSource } from 'lightweight-charts';
import { Button, Checkbox } from 'antd';
import Tooltip from './Tooltip/Tooltip';

import { COLORS, SERIES_POSITION } from './Consts/LineChartConsts';
const RESIZE_RENDER_DELAY = 150;

class LineChart extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			hidden: false,
			visibleSeries: {
				temp: true,
				rh: true,
				ppm: true
			},
			isRHWithTempOnly: false
		}

		window.LC = this;

		this.resizeTimer = null;
		this.changeVisibleSeriesState = this.changeVisibleSeriesState.bind(this);
		this.resizeListener = this.resizeListener.bind(this);
		this.lineChartRef = React.createRef();
	}

	componentDidMount() {
		this.createChart();
		this.createAllSeries();
		this.createTooltip();
		this.handleResize();
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.data !== this.props.data) {
			this.ppmSeries.setData(this.props.data.ppm);
			// console.log(this.props.data.ppm.slice(1300, 1470))
			this.tempSeries.setData(this.props.data.temp);
			this.rhSeries.setData(this.props.data.rh);
		}

		const sizeHasChanged = prevProps.width !== this.props.width || prevProps.height !== this.props.height;
		const sizeExists = this.props.width && this.props.height;

		if (sizeHasChanged && sizeExists) {
			// console.log('Resize')
			this.chart.resize(this.props.width, this.props.height);

		}

		if (prevState.visibleSeries !== this.state.visibleSeries) {
			this.updateSeriesVisibility();
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.resizeListener);
	}

	handleResize() {
		window.addEventListener('resize', this.resizeListener);
	}

	resizeListener() {
		if (!this.state.hidden) {
			this.setState({hidden: true})
		}

		clearTimeout(this.resizeTimer);
		this.resizeTimer = setTimeout(() => {
			this.setState({hidden: false})
		}, RESIZE_RENDER_DELAY);
	}

	changeVisibleSeriesState(series, value) {
		this.setState(prevState => {
      return {
        visibleSeries : Object.assign({}, prevState.visibleSeries, {
					[series]: value
				})
      };
    });
	}

	updateSeriesVisibility() {
		const visibleSeries = this.state.visibleSeries;
		const useRHWithTempOnly = visibleSeries.temp && visibleSeries.rh && !visibleSeries.ppm;

		for (const [key, value] of Object.entries(visibleSeries)) {
			this[`${key}Series`].applyOptions({
				visible: value,
			});
		}		

		if (useRHWithTempOnly) { //TODO refactor
			this.rhSeries.applyOptions({
				priceScaleId: SERIES_POSITION.LEFT
			});
			this.setState({ isRHWithTempOnly: true});
		} else if (this.state.isRHWithTempOnly) {
			this.rhSeries.applyOptions({
				priceScaleId: SERIES_POSITION.RIGHT
			});
			this.setState({ isRHWithTempOnly : false})
		}
	}

	createLineSeries(title, seriesPosition, color) {
		return this.chart.addLineSeries({
			title,
			color,
			priceScaleId: seriesPosition,
			priceLineVisible: true,
			lastValueVisible: true,
			priceLineSource: PriceLineSource.LastVisible
		});
	}

	tickMarkFormatter(timestamp, tickMarkType, locale) { //TODO move to utils
		const date = new Date(timestamp * 1000);					

		switch (tickMarkType) {
			case TickMarkType.Year:
				return date.getFullYear();

			case TickMarkType.Month:
				const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'short' });
				
				return monthFormatter.format(date);

			case TickMarkType.DayOfMonth:
				return date.getDate();

			case TickMarkType.Time:
				const timeFormatter = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "numeric" });
				
				return timeFormatter.format(date);

			case TickMarkType.TimeWithSeconds:
				const timeWithSecondsFormatter = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "numeric", second: "numeric"});

				return timeWithSecondsFormatter.format(date);
		}

		throw new Error('Unhandled tick mark type ' + tickMarkType);
	}

	timeFormatter(timestamp) { //TODO move to utils
		const date = new Date(timestamp * 1000);
		const dateFormatter = new Intl.DateTimeFormat(navigator.language, {
			hour: 'numeric',
			minute: 'numeric', 
			month: 'short', 
			day: 'numeric', 
			year: '2-digit'
		 });

		 return dateFormatter.format(date);
	}

	createChart() {
		this.chart = createChart(this.lineChartRef.current, { 
			width: this.props.width, 
			height: this.props.height,
			timeScale: {
				timeVisible: true,				
				secondsVisible: false,
				tickMarkFormatter: this.tickMarkFormatter,
			},
			leftPriceScale: {
				visible: true,
			},
			localization: {
        timeFormatter: this.timeFormatter,
   		},
			watermark: {
				visible: true,
				// fontWeight: 300,
				// fontFamily: '',
				fontSize: 34,
				horzAlign: 'center',
				vertAlign: 'center',
				// color: 'rgba(0, 0, 0, 0.6)',
				color: 'rgba(53, 54, 52, 0.7)',
				text: 'Hala 1'
			}
		});
	}

	createAllSeries() {
		this.ppmSeries = this.createLineSeries('PPM', SERIES_POSITION.LEFT, COLORS.BLUE);
		this.tempSeries = this.createLineSeries('°C', SERIES_POSITION.RIGHT, COLORS.RED);
		this.rhSeries = this.createLineSeries('RH', SERIES_POSITION.RIGHT, COLORS.YELLOW);
	}

	createTooltip() {
		const toolTip = new Tooltip({
			chart: this.chart,
			container: this.lineChartRef.current,
			ppmSeries: this.ppmSeries,
			tempSeries: this.tempSeries,
			rhSeries: this.rhSeries,
			getSize: () => ({ //TMP lightweight-chart has bug -> options size is not updated
				width: this.props.width,
				height: this.props.height
			})
		});

		toolTip.render();
	}

	renderChart() {
		return (
			<div className={`line-chart ${this.state.hidden ? 'line-chart--hidden' : ''}`} ref={this.lineChartRef}>
					{ this.renderSettings() }
			</div>
		)
	}

	getSeriesVisibilityCheckbox(title, property) {
		const checked = this.state.visibleSeries[property];

		return (
			<Checkbox 
				checked={checked} 
				onChange={() => this.changeVisibleSeriesState(property, !checked)}
			>
				{ title }
			</Checkbox>
		)
	}

	renderSettings() {
		return (
			<div className="line-chart__settings"> 
				{ this.getSeriesVisibilityCheckbox('°C', 'temp') }
				{ this.getSeriesVisibilityCheckbox('RH', 'rh') }
				{ this.getSeriesVisibilityCheckbox('PPM', 'ppm') }
			</div>
		)
	}

	render() {
		return (
			<React.Fragment>
				{ this.renderChart() }
			</React.Fragment>
		)
	}
}

LineChart.defaultProps = {
	data: [],
	width: 0,
	height: 0
}

export default LineChart;

// // Real time
// let lastTime = 1559055600;
// setInterval(() => {
// 	lastTime+= 60;

// 	const noise = (0.1 - Math.random() * 0.2) + 1.0;

// 	lineSeries.update({
// 		time: lastTime,
// 		value: 230 * noise,
// 	});
// }, 2000)